/**
 * eReg API Client
 *
 * All API calls for the eReg platform.
 */

import api from '../../services/api';
import type {
  Protocol,
  Folder,
  Document,
  User,
  DocumentType,
  FolderTemplate,
  AuditTrailEntry,
  MetadataSuggestion,
  DocumentUrlResponse,
  DocumentVersionsResponse,
  UploadVersionResponse,
  ProtocolAccess,
  SignatureQueueItem,
  Signature,
  SignatureRequest,
  SignatureAssignment,
  SignResponse,
  BatchSignResponse,
  SignatureMeaning,
  DelegationLog,
  DelegationTask,
  DelegationEntry,
  DelegationRequiredDoc,
  DelegationSignRole,
  IntakeItem,
  IntakeFilePayload,
  AuditEntry,
  SearchResponse,
  DashboardStatsResponse,
} from '../types';

const EREG_BASE = '/api/ereg';

// =====================================================
// PROTOCOL HELPERS
// =====================================================

/**
 * Normalize protocol response to flatten ereg_config for backward compatibility
 */
function normalizeProtocol(protocol: Protocol): Protocol {
  if (!protocol) return protocol;

  // If protocol has ereg_config, extract common fields for convenience
  if (protocol.ereg_config) {
    return {
      ...protocol,
      // Map study_nickname to protocol_title for backward compatibility
      protocol_title: protocol.study_nickname,
      // Flatten eReg-specific fields for convenience
      primary_investigator_id: protocol.ereg_config.primary_investigator_id,
      primary_crc_id: protocol.ereg_config.primary_crc_id,
      irb_of_record: protocol.ereg_config.irb_of_record,
      ingest_email_address: protocol.ereg_config.ingest_email_address,
      site_intelligence_enabled: protocol.ereg_config.site_intelligence_enabled,
      created_by: protocol.ereg_config.created_by,
      // Use ereg_status as the main status
      status: protocol.ereg_config.ereg_status,
    };
  }

  return protocol;
}

// =====================================================
// PROTOCOL API
// =====================================================

export async function getProtocols(opts?: { includeArchived?: boolean }): Promise<Protocol[]> {
  const response = await api.get(`${EREG_BASE}/protocols`, {
    params: opts?.includeArchived ? { include_archived: 'true' } : undefined,
  });
  return (response.data || []).map(normalizeProtocol);
}

export async function getProtocol(id: string): Promise<Protocol> {
  const response = await api.get(`${EREG_BASE}/protocols/${id}`);
  return normalizeProtocol(response.data);
}

export async function createProtocol(data: Partial<Protocol> & { template_id?: string }): Promise<Protocol> {
  // Map protocol_title to the expected field name
  const payload = {
    ...data,
    protocol_title: data.protocol_title || data.study_nickname,
  };
  const response = await api.post(`${EREG_BASE}/protocols`, payload);
  return normalizeProtocol(response.data);
}

export async function updateProtocol(id: string, data: Partial<Protocol>): Promise<Protocol> {
  // Map protocol_title to the expected field name
  const payload = {
    ...data,
    protocol_title: data.protocol_title || data.study_nickname,
  };
  const response = await api.patch(`${EREG_BASE}/protocols/${id}`, payload);
  return normalizeProtocol(response.data);
}

/**
 * Registry soft-archive toggle. Archiving hides the study from the registry
 * list / StudyPicker (and refuses new IXVerify links) but keeps it and every
 * existing link intact; pass `archived: false` to restore it.
 */
export async function archiveProtocol(id: string, archived: boolean): Promise<Protocol> {
  const response = await api.patch(`${EREG_BASE}/protocols/${id}/archive`, { archived });
  return normalizeProtocol(response.data);
}

/**
 * Star/unstar a study for the current user — sorts it to the top of that
 * user's own "My Studies" list on the Dashboard. Personal preference only.
 */
export async function starProtocol(id: string): Promise<{ starred_at: string }> {
  const response = await api.put(`${EREG_BASE}/protocols/${id}/star`);
  return response.data;
}

export async function unstarProtocol(id: string): Promise<{ starred_at: null }> {
  const response = await api.delete(`${EREG_BASE}/protocols/${id}/star`);
  return response.data;
}

export interface DeleteProtocolResponse {
  success: boolean;
  deleted_protocol_id: string;
  s3_objects_deleted: number;
}

/**
 * Permanently delete a study (super_admin only, server-enforced).
 * `confirmProtocolNumber` must equal the study's protocol_number exactly, and
 * `force` re-confirms deletion of a study still referenced by other modules
 * (the server 409s with `code: 'EREG_STUDY_IN_USE'` + a links payload otherwise).
 */
export async function deleteProtocol(
  id: string,
  confirmProtocolNumber: string,
  options?: { force?: boolean }
): Promise<DeleteProtocolResponse> {
  const response = await api.delete(`${EREG_BASE}/protocols/${id}`, {
    data: {
      confirm_protocol_number: confirmProtocolNumber,
      force: options?.force === true,
    },
  });
  return response.data;
}

// =====================================================
// FOLDER API
// =====================================================

export async function getProtocolFolders(protocolId: string): Promise<Folder[]> {
  const response = await api.get(`${EREG_BASE}/protocols/${protocolId}/folders`);
  return response.data;
}

export async function getSiteFolders(siteId: string): Promise<Folder[]> {
  const response = await api.get(`${EREG_BASE}/sites/${siteId}/folders`);
  return response.data;
}

export async function createFolder(data: {
  protocol_id?: string;
  site_id?: string;
  /** Network-zone folders (Phase 5) — admin-gated server-side. */
  network_id?: string;
  parent_folder_id?: string;
  name: string;
  allowed_document_types?: string[];
  default_document_type?: string;
  default_expiration_tracking?: boolean;
  sort_order?: number;
}): Promise<Folder> {
  const response = await api.post(`${EREG_BASE}/folders`, data);
  return response.data;
}

export async function updateFolder(id: string, data: Partial<Folder>): Promise<Folder> {
  const response = await api.patch(`${EREG_BASE}/folders/${id}`, data);
  return response.data;
}

export async function deleteFolder(id: string): Promise<void> {
  await api.delete(`${EREG_BASE}/folders/${id}`);
}

// =====================================================
// DOCUMENT API
// =====================================================

export async function getFolderDocuments(
  folderId: string,
  options?: { include_archived?: boolean }
): Promise<Document[]> {
  const params = new URLSearchParams();
  if (options?.include_archived) {
    params.append('include_archived', 'true');
  }
  const response = await api.get(`${EREG_BASE}/folders/${folderId}/documents?${params}`);
  return response.data;
}

export async function getDocument(id: string): Promise<Document> {
  const response = await api.get(`${EREG_BASE}/documents/${id}`);
  return response.data;
}

export async function getDocumentViewUrl(id: string, signed?: boolean): Promise<DocumentUrlResponse> {
  const params = signed ? '?signed=true' : '';
  const response = await api.get(`${EREG_BASE}/documents/${id}/view${params}`);
  return response.data;
}

export async function getDocumentDownloadUrl(id: string, signed?: boolean): Promise<DocumentUrlResponse> {
  const params = signed ? '?signed=true' : '';
  const response = await api.get(`${EREG_BASE}/documents/${id}/download${params}`);
  return response.data;
}

export async function uploadDocument(
  file: File,
  metadata: {
    folder_id: string;
    document_name: string;
    document_type: string;
    document_date?: string;
    version_number?: string;
    expiration_date?: string;
    document_owner_id?: string;
    description?: string;
    fulfills_placeholder_id?: string;
  }
): Promise<Document> {
  const formData = new FormData();
  formData.append('file', file);
  Object.entries(metadata).forEach(([key, value]) => {
    if (value !== undefined && value !== null) {
      formData.append(key, value);
    }
  });

  const response = await api.post(`${EREG_BASE}/documents`, formData, {
    headers: {
      'Content-Type': 'multipart/form-data',
    },
  });
  return response.data;
}

export async function updateDocument(id: string, data: Partial<Document>): Promise<Document> {
  const response = await api.patch(`${EREG_BASE}/documents/${id}`, data);
  return response.data;
}

export async function deleteDocument(id: string): Promise<void> {
  await api.delete(`${EREG_BASE}/documents/${id}`);
}

export async function restoreDocument(id: string): Promise<Document> {
  const response = await api.post(`${EREG_BASE}/documents/${id}/restore`);
  return response.data;
}

export async function uploadDocumentVersion(
  id: string,
  file: File,
  versionNumber: string
): Promise<UploadVersionResponse> {
  const formData = new FormData();
  formData.append('file', file);
  formData.append('version_number', versionNumber);

  const response = await api.post(`${EREG_BASE}/documents/${id}/upload-version`, formData, {
    headers: {
      'Content-Type': 'multipart/form-data',
    },
  });
  return response.data;
}

// =====================================================
// VERSION HISTORY (Phase 3)
// =====================================================

export async function getDocumentVersions(id: string): Promise<DocumentVersionsResponse> {
  const response = await api.get(`${EREG_BASE}/documents/${id}/versions`);
  return response.data;
}

/** Presigned view URL for a HISTORICAL version's bytes (audited like /view). */
export async function getVersionViewUrl(
  documentId: string,
  versionId: string
): Promise<DocumentUrlResponse & { version_number?: string }> {
  const response = await api.get(`${EREG_BASE}/documents/${documentId}/versions/${versionId}/view`);
  return response.data;
}

// =====================================================
// DOCUMENT TYPES API
// =====================================================

export async function getDocumentTypes(options?: {
  network_id?: string;
  site_id?: string;
}): Promise<DocumentType[]> {
  const params = new URLSearchParams();
  if (options?.network_id) params.append('network_id', options.network_id);
  if (options?.site_id) params.append('site_id', options.site_id);

  const response = await api.get(`${EREG_BASE}/document-types?${params}`);
  return response.data;
}

export async function createDocumentType(data: {
  name: string;
  network_id?: string;
  site_id?: string;
}): Promise<DocumentType> {
  const response = await api.post(`${EREG_BASE}/document-types`, data);
  return response.data;
}

// =====================================================
// AI METADATA SUGGESTION
// =====================================================

export async function suggestMetadata(file: File): Promise<MetadataSuggestion> {
  const formData = new FormData();
  formData.append('file', file);

  const response = await api.post(`${EREG_BASE}/suggest-metadata`, formData, {
    headers: {
      'Content-Type': 'multipart/form-data',
    },
  });
  return response.data;
}

export interface StudyFieldSuggestions {
  protocol_number?: string | null;
  protocol_title?: string | null;
  sponsor_name?: string | null;
  phase?: string | null;
  therapeutic_area?: string | null;
  irb_of_record?: string | null;
}

/**
 * AI-extract study registry fields from a dropped protocol PDF, to prefill the
 * create-study form. Returns `{ fields }` (empty when the file isn't a PDF or
 * extraction fails — the endpoint is fail-soft).
 */
export async function suggestStudyFields(
  file: File
): Promise<{ fields: StudyFieldSuggestions; unsupported?: boolean }> {
  const formData = new FormData();
  formData.append('file', file);

  const response = await api.post(`${EREG_BASE}/suggest-study-fields`, formData, {
    headers: {
      'Content-Type': 'multipart/form-data',
    },
  });
  return response.data;
}

// =====================================================
// AUDIT TRAIL API
// =====================================================

export async function getDocumentAuditTrail(documentId: string): Promise<AuditTrailEntry[]> {
  const response = await api.get(`${EREG_BASE}/documents/${documentId}/audit-trail`);
  return response.data;
}

export async function getProtocolAuditTrail(
  protocolId: string,
  limit?: number
): Promise<AuditTrailEntry[]> {
  const params = limit ? `?limit=${limit}` : '';
  const response = await api.get(`${EREG_BASE}/protocols/${protocolId}/audit-trail${params}`);
  return response.data;
}

// =====================================================
// FOLDER TEMPLATES API
// =====================================================

export async function getFolderTemplates(options?: {
  scope?: 'global' | 'network' | 'site';
  scope_id?: string;
  is_site_template?: boolean;
  /** Narrow to templates usable at this site (global + the site's + its network's). */
  site_id?: string;
}): Promise<FolderTemplate[]> {
  const params = new URLSearchParams();
  if (options?.scope) params.append('scope', options.scope);
  if (options?.scope_id) params.append('scope_id', options.scope_id);
  if (options?.is_site_template !== undefined) {
    params.append('is_site_template', String(options.is_site_template));
  }
  if (options?.site_id) params.append('site_id', options.site_id);

  const response = await api.get(`${EREG_BASE}/folder-templates?${params}`);
  return response.data;
}

export async function createFolderTemplate(data: Partial<FolderTemplate>): Promise<FolderTemplate> {
  const response = await api.post(`${EREG_BASE}/folder-templates`, data);
  return response.data;
}

export async function applyFolderTemplate(protocolId: string, templateId: string): Promise<void> {
  await api.post(`${EREG_BASE}/protocols/${protocolId}/apply-template`, {
    template_id: templateId,
  });
}

// =====================================================
// PROTOCOL ACCESS API
// =====================================================

export async function getProtocolAccess(protocolId: string): Promise<ProtocolAccess[]> {
  const response = await api.get(`${EREG_BASE}/protocols/${protocolId}/access`);
  return response.data;
}

export async function grantProtocolAccess(
  protocolId: string,
  userId: string,
  accessLevel: 'read_only' | 'edit'
): Promise<ProtocolAccess> {
  const response = await api.post(`${EREG_BASE}/protocols/${protocolId}/access`, {
    user_id: userId,
    access_level: accessLevel,
  });
  return response.data;
}

export async function revokeProtocolAccess(protocolId: string, userId: string): Promise<void> {
  await api.delete(`${EREG_BASE}/protocols/${protocolId}/access/${userId}`);
}

// =====================================================
// SIGNATURE API
// =====================================================

export async function getSignatureQueue(): Promise<SignatureQueueItem[]> {
  const response = await api.get(`${EREG_BASE}/signature-queue`);
  return response.data;
}

export async function getDocumentSignatures(documentId: string): Promise<Signature[]> {
  const response = await api.get(`${EREG_BASE}/documents/${documentId}/signatures`);
  return response.data;
}

export async function getSignatureMeanings(): Promise<string[]> {
  const response = await api.get(`${EREG_BASE}/signature-meanings`);
  return response.data;
}

export async function getEligibleSigners(siteId: string): Promise<User[]> {
  const response = await api.get(`${EREG_BASE}/sites/${siteId}/eligible-signers`);
  return response.data;
}

export async function requestSignature(data: {
  document_id: string;
  signers: Array<{
    user_id: string;
    signature_meaning?: SignatureMeaning;
    order_number?: number;
  }>;
  message?: string;
  due_date?: string;
}): Promise<{ signatureRequest: SignatureRequest; assignments: SignatureAssignment[] }> {
  const response = await api.post(`${EREG_BASE}/signatures/request`, data);
  return response.data;
}

export async function signDocument(data: {
  assignment_id: string;
  password: string;
  signature_meaning?: SignatureMeaning;
  page_number?: number;
  x?: number;
  y?: number;
}): Promise<SignResponse> {
  const response = await api.post(`${EREG_BASE}/signatures/sign`, data);
  return response.data;
}

export async function batchSignDocuments(data: {
  assignment_ids: string[];
  password: string;
}): Promise<BatchSignResponse> {
  const response = await api.post(`${EREG_BASE}/signatures/batch-sign`, data);
  return response.data;
}

export async function declineSignature(
  assignmentId: string,
  reason?: string
): Promise<SignatureAssignment> {
  const response = await api.post(`${EREG_BASE}/signatures/decline`, {
    assignment_id: assignmentId,
    reason,
  });
  return response.data;
}

// =====================================================
// DELEGATION LOG API
// =====================================================

export async function getDelegationLog(protocolId: string): Promise<DelegationLog | null> {
  const response = await api.get(`${EREG_BASE}/delegation-logs/${protocolId}`);
  return response.data;
}

export async function createDelegationLog(data: {
  protocol_id: string;
  pdf_config?: Record<string, unknown>;
}): Promise<DelegationLog> {
  const response = await api.post(`${EREG_BASE}/delegation-logs`, data);
  return response.data;
}

export async function getDelegationTasks(delegationLogId: string): Promise<DelegationTask[]> {
  const response = await api.get(`${EREG_BASE}/delegation-logs/${delegationLogId}/tasks`);
  return response.data;
}

/**
 * Diff-based task editor (Phase 3): tasks WITH an id update in place, id-less
 * tasks insert, and omitted tasks delete — but only when no entry delegates
 * them (409 EREG_TASKS_IN_USE otherwise).
 */
export async function updateDelegationTasks(
  delegationLogId: string,
  tasks: Array<{
    id?: string;
    task_number: number;
    task_description: string;
    required_document_types?: string[];
  }>
): Promise<DelegationTask[]> {
  const response = await api.post(`${EREG_BASE}/delegation-logs/${delegationLogId}/tasks`, {
    tasks,
  });
  return response.data;
}

export async function addDelegationEntry(
  delegationLogId: string,
  data: {
    user_id?: string;
    external_name?: string;
    external_email?: string;
    role: string;
    role_other?: string;
    delegated_task_ids: string[];
    start_date: string;
    end_date?: string;
  }
): Promise<DelegationEntry> {
  const response = await api.post(`${EREG_BASE}/delegation-logs/${delegationLogId}/entries`, data);
  return response.data;
}

export async function updateDelegationEntry(
  delegationLogId: string,
  entryId: string,
  data: Partial<DelegationEntry>
): Promise<DelegationEntry> {
  const response = await api.patch(
    `${EREG_BASE}/delegation-logs/${delegationLogId}/entries/${entryId}`,
    data
  );
  return response.data;
}

export async function linkDocumentToDelegation(
  delegationLogId: string,
  entryId: string,
  requiredDocId: string,
  documentId: string
): Promise<DelegationRequiredDoc> {
  const response = await api.post(
    `${EREG_BASE}/delegation-logs/${delegationLogId}/entries/${entryId}/link-document`,
    {
      required_doc_id: requiredDocId,
      document_id: documentId,
    }
  );
  return response.data;
}

export async function getDelegationRoles(): Promise<string[]> {
  const response = await api.get(`${EREG_BASE}/delegation-roles`);
  return response.data;
}

export async function signDelegationEntry(
  delegationLogId: string,
  entryId: string,
  data: { password: string; role: DelegationSignRole }
): Promise<{ entry: DelegationEntry; activated: boolean; ended?: boolean }> {
  const response = await api.post(
    `${EREG_BASE}/delegation-logs/${delegationLogId}/entries/${entryId}/sign`,
    data
  );
  return response.data;
}

/**
 * TMF-7: record that an external (Sponsor/CRO) member's sign-offs happened on
 * the printed log (wet-ink). Admin or PI only; activates the entry.
 */
export async function recordWetInkSignOff(
  delegationLogId: string,
  entryId: string,
  note?: string
): Promise<{ entry: DelegationEntry }> {
  const response = await api.post(
    `${EREG_BASE}/delegation-logs/${delegationLogId}/entries/${entryId}/record-wet-ink`,
    { note: note || undefined }
  );
  return response.data;
}

export async function exportDelegationLogPdf(protocolId: string): Promise<Blob> {
  const response = await api.get(`${EREG_BASE}/delegation-logs/${protocolId}/export-pdf`, {
    responseType: 'blob',
  });
  return response.data;
}

// =====================================================
// UNIFIED INTAKE API (Phase 5 — replaced the email ingest queue)
// =====================================================

export interface IntakeUploadResponse {
  items: IntakeItem[];
  batch_id: string;
  errors?: Array<{ file: string; error: string }>;
}

export interface IntakeFileResponse {
  document: Document;
  /** Present when the upload's type maps to a key-document slot (§3.2). */
  key_document?: {
    role?: string;
    occupied_by_document_id?: string | null;
  } | null;
}

export async function getIntakeItems(
  siteId: string,
  status: 'pending' | 'filed' | 'discarded' | 'all' = 'pending'
): Promise<IntakeItem[]> {
  const params = new URLSearchParams({ site_id: siteId, status });
  const response = await api.get(`${EREG_BASE}/intake?${params}`);
  return response.data;
}

export async function uploadIntakeFiles(siteId: string, files: File[]): Promise<IntakeUploadResponse> {
  const formData = new FormData();
  formData.append('site_id', siteId);
  files.forEach((file) => formData.append('files', file));
  const response = await api.post(`${EREG_BASE}/intake/upload`, formData, {
    headers: {
      'Content-Type': 'multipart/form-data',
    },
  });
  return response.data;
}

export async function getIntakePreviewUrl(id: string): Promise<{ url: string; mime_type?: string }> {
  const response = await api.get(`${EREG_BASE}/intake/${id}/preview-url`);
  return response.data;
}

export async function fileIntakeItem(id: string, payload: IntakeFilePayload): Promise<IntakeFileResponse> {
  const response = await api.post(`${EREG_BASE}/intake/${id}/file`, payload);
  return response.data;
}

export async function discardIntakeItem(id: string): Promise<IntakeItem> {
  const response = await api.post(`${EREG_BASE}/intake/${id}/discard`);
  return response.data;
}

/** P-9: undo a discard (bytes are retained 30 days; 410 once purged). */
export async function restoreIntakeItem(id: string): Promise<IntakeItem> {
  const response = await api.post(`${EREG_BASE}/intake/${id}/restore`);
  return response.data;
}

// =====================================================
// NETWORK DOCUMENTS API (Phase 5)
// =====================================================

export async function getNetworkFolders(networkId: string): Promise<Folder[]> {
  const response = await api.get(`${EREG_BASE}/networks/${networkId}/folders`);
  return response.data;
}

// =====================================================
// SITE-WIDE AUDIT TRAIL API (Phase 5)
// =====================================================

export interface AuditTrailParams {
  site_id?: string;
  protocol_id?: string;
  document_id?: string;
  action?: string;
  user_id?: string;
  from?: string;
  to?: string;
  limit?: number;
  offset?: number;
}

export async function getAuditTrail(
  params: AuditTrailParams
): Promise<{ items: AuditEntry[]; has_more: boolean }> {
  const queryParams = new URLSearchParams();
  Object.entries(params).forEach(([key, value]) => {
    if (value !== undefined && value !== null && value !== '') {
      queryParams.append(key, String(value));
    }
  });
  const response = await api.get(`${EREG_BASE}/audit?${queryParams}`);
  return response.data;
}

// (The eReg→Intelligence copy-sync wrappers were removed in P3 — Intelligence
// reads the registry directly; see docs/ereg-source-of-truth-plan.md §5.2.)

// =====================================================
// SEARCH API
// =====================================================

export async function searchDocuments(params: {
  query: string;
  protocol_id?: string;
  site_id?: string;
  folder_id?: string;
  document_type?: string;
  status?: string;
  limit?: number;
}): Promise<SearchResponse> {
  const queryParams = new URLSearchParams();
  queryParams.append('query', params.query);
  if (params.protocol_id) queryParams.append('protocol_id', params.protocol_id);
  if (params.site_id) queryParams.append('site_id', params.site_id);
  if (params.folder_id) queryParams.append('folder_id', params.folder_id);
  if (params.document_type) queryParams.append('document_type', params.document_type);
  if (params.status) queryParams.append('status', params.status);
  if (params.limit) queryParams.append('limit', params.limit.toString());

  const response = await api.get(`${EREG_BASE}/search?${queryParams}`);
  return response.data;
}

// =====================================================
// DASHBOARD API
// =====================================================

export async function getDashboardStats(params?: {
  site_id?: string;
  protocol_id?: string;
}): Promise<DashboardStatsResponse> {
  const queryParams = new URLSearchParams();
  if (params?.site_id) queryParams.append('site_id', params.site_id);
  if (params?.protocol_id) queryParams.append('protocol_id', params.protocol_id);

  const queryString = queryParams.toString();
  const response = await api.get(`${EREG_BASE}/dashboard/stats${queryString ? `?${queryString}` : ''}`);
  return response.data;
}

// =====================================================
// KEY DOCUMENTS + LINKS + TEXT EXTRACTION (P1)
// =====================================================

export interface KeyDocumentSlot {
  id: string;
  protocol_id: string;
  role: string;
  variant_label: string;
  document_id: string;
  set_by?: string | null;
  set_at: string;
  document?: {
    id: string;
    document_name: string;
    document_type: string;
    version_number: string;
    status: string;
    deleted_at?: string | null;
    updated_at?: string;
  } | null;
}

export interface KeyDocumentsResponse {
  roles: string[];
  role_labels: Record<string, string>;
  slots: KeyDocumentSlot[];
}

export interface StudyLinks {
  crm: { id: string; slug: string; title: string; status: string }[];
  organizer: { team_members: number; bookmarks: number };
  ixverify: { id: string }[];
  status_board: string[];
  intelligence_documents: number;
  /** MHR protocols linked via core_protocol_id (cross-DB count; P4 §5.6). */
  mhr?: number;
}

export async function getKeyDocuments(protocolId: string): Promise<KeyDocumentsResponse> {
  const response = await api.get(`${EREG_BASE}/protocols/${protocolId}/key-documents`);
  return response.data;
}

export async function setKeyDocument(
  protocolId: string,
  role: string,
  data: { document_id: string; variant_label?: string }
): Promise<KeyDocumentSlot> {
  const response = await api.put(`${EREG_BASE}/protocols/${protocolId}/key-documents/${role}`, data);
  return response.data;
}

export async function clearKeyDocument(
  protocolId: string,
  role: string,
  variantLabel?: string
): Promise<{ cleared: boolean }> {
  const qs = variantLabel ? `?variant_label=${encodeURIComponent(variantLabel)}` : '';
  const response = await api.delete(`${EREG_BASE}/protocols/${protocolId}/key-documents/${role}${qs}`);
  return response.data;
}

export async function getStudyLinks(protocolId: string): Promise<StudyLinks> {
  const response = await api.get(`${EREG_BASE}/protocols/${protocolId}/links`);
  return response.data;
}

export async function reextractDocumentText(documentId: string): Promise<{ queued: boolean }> {
  const response = await api.post(`${EREG_BASE}/documents/${documentId}/reextract-text`);
  return response.data;
}

// Export all functions
export default {
  // Protocols
  getProtocols,
  getProtocol,
  createProtocol,
  updateProtocol,
  archiveProtocol,
  deleteProtocol,
  // Folders
  getProtocolFolders,
  getSiteFolders,
  createFolder,
  updateFolder,
  deleteFolder,
  // Documents
  getFolderDocuments,
  getDocument,
  getDocumentViewUrl,
  getDocumentDownloadUrl,
  uploadDocument,
  updateDocument,
  deleteDocument,
  restoreDocument,
  uploadDocumentVersion,
  getDocumentVersions,
  getVersionViewUrl,
  // Document Types
  getDocumentTypes,
  createDocumentType,
  // AI
  suggestMetadata,
  suggestStudyFields,
  // Audit Trail
  getDocumentAuditTrail,
  getProtocolAuditTrail,
  // Templates
  getFolderTemplates,
  createFolderTemplate,
  // Access
  getProtocolAccess,
  grantProtocolAccess,
  revokeProtocolAccess,
  // Signatures
  getSignatureQueue,
  getDocumentSignatures,
  getSignatureMeanings,
  getEligibleSigners,
  requestSignature,
  signDocument,
  batchSignDocuments,
  declineSignature,
  // Delegation Log
  getDelegationLog,
  createDelegationLog,
  getDelegationTasks,
  updateDelegationTasks,
  addDelegationEntry,
  updateDelegationEntry,
  linkDocumentToDelegation,
  getDelegationRoles,
  signDelegationEntry,
  recordWetInkSignOff,
  exportDelegationLogPdf,
  // Unified Intake (Phase 5)
  getIntakeItems,
  uploadIntakeFiles,
  getIntakePreviewUrl,
  fileIntakeItem,
  discardIntakeItem,
  restoreIntakeItem,
  // Network Documents (Phase 5)
  getNetworkFolders,
  // Site-wide Audit Trail (Phase 5)
  getAuditTrail,
  // Search
  searchDocuments,
  // Dashboard
  getDashboardStats,
  // Key documents + links + extraction (P1)
  getKeyDocuments,
  setKeyDocument,
  clearKeyDocument,
  getStudyLinks,
  reextractDocumentText,
};
